import {
  AbstractAccountKeyring,
  AbstractPeerAccountKeyring,
  AbstractSelfAccountKeyring,
} from './account_base';
import { ACCOUNT_SIGNATURE_CRYPTO_CONTEXT } from '../context/account_signature/__init__';
import {
  AccountSignatureConfigDumpArgs,
  AccountSignatureConfigLoadArgs,
} from '../context/account_signature/args';
import { ACCOUNT_TRUST_CRYPTO_CONTEXT } from '../context/account_trust/__init__';
import { AccountTrustConfigLoadArgs } from '../context/account_trust/args';
import { TrustIsStale, TrustNeedsUpgrade } from '../context/account_trust/common';
import { ArgumentsError } from '../context/base';
import { CryptoUpgrade, CU_PQC_ML } from '../context/feature';
import { compareByteArray, concatByteArray } from '../utility';
import { MLKEM1024KEM } from '../base/key/kem';
import { SHARED_SECRET_CRYPTO_CONTEXT } from '../context/shared_secret/__init__';
import {
  SharedSecretConfigDumpArgs,
  SharedSecretConfigLoadArgs,
} from '../context/shared_secret/args';

/**
 * A peer account's cryptographic primitives are their public keys for encryption and signature verification.
 *
 * The PQC extension includes shared-secret exchanges using KEM. This is a little less convenient to handle,
 * because the initiating party has to send the KEM message with the request, and each party may do the initiation
 * in certain cases, making two different shared secrets exist for the same pair of peers. Using any or both of the
 * shared-secrets would be secure, because we have the KEM message signed as well for authenticity.
 */
export class PeerAccountKeyring extends AbstractPeerAccountKeyring {
  /**
     * Loading a peer account's keyring requires checking the signatures of all keys by the trust root and
        verifying the trust-fingerprint of the root if present.
        After new keys and signing roots have been introduced the old ones can become optional.
        The caller and all codes must check the trusted state before using the keyring.

     */
  public static async load(
    self_kr: AbstractSelfAccountKeyring,
    account_id: string,
    curve25519_public: Uint8Array,
    curve25519_public_sig: Uint8Array,
    ed25519_public: Uint8Array,
    trust_fingerprint?: Uint8Array,
    stored_tofu_fingerprints?: Object,
    // PQC extensions; all *_sig fields are dual signed in this case
    mlkem1024_public?: Uint8Array,
    mlkem1024_public_sig?: Uint8Array,
    mldsa87_public?: Uint8Array,
    ed25519_public_mldsa87_public_sig?: Uint8Array,
    // shared secrets from MLKEM if any
    mlkem1024_ss_self?: Uint8Array, // generated and encrypted by self_kr
    mlkem1024_ss_peer?: Uint8Array, // generated (+_sig) by peer; later encrypted by self_kr (optimized)
    mlkem1024_ss_peer_sig?: Uint8Array // signature by peer
  ): Promise<PeerAccountKeyring> {
    if (
      mlkem1024_public ||
      mlkem1024_public_sig ||
      mldsa87_public ||
      ed25519_public_mldsa87_public_sig
    ) {
      if (
        !(
          mlkem1024_public &&
          mlkem1024_public_sig &&
          mldsa87_public &&
          ed25519_public_mldsa87_public_sig
        )
      ) {
        throw Error('ML keys need to be all present');
      }
    } else if (mlkem1024_ss_self || mlkem1024_ss_peer || mlkem1024_ss_peer_sig) {
      throw Error('ML shared-secrets require ML keys');
    }
    let public_keys = {};
    public_keys[PeerAccountKeyring.CURVE25519] = curve25519_public;
    public_keys[PeerAccountKeyring.ED25519] = ed25519_public;
    let required_cu: CryptoUpgrade;
    if (mlkem1024_public) {
      public_keys[PeerAccountKeyring.MLKEM1024] = mlkem1024_public;
      public_keys[PeerAccountKeyring.MLDSA87] = mldsa87_public;
      required_cu = CU_PQC_ML;
    }
    let peer_kr = new PeerAccountKeyring(account_id, public_keys);
    // Signatures of all present keys are required regardless of trust.
    await ACCOUNT_SIGNATURE_CRYPTO_CONTEXT.load(
      new AccountSignatureConfigLoadArgs(curve25519_public_sig, peer_kr, curve25519_public),
      required_cu
    );
    if (mlkem1024_public) {
      await ACCOUNT_SIGNATURE_CRYPTO_CONTEXT.load(
        new AccountSignatureConfigLoadArgs(mlkem1024_public_sig, peer_kr, mlkem1024_public),
        required_cu
      );
      await ACCOUNT_SIGNATURE_CRYPTO_CONTEXT.load(
        new AccountSignatureConfigLoadArgs(
          ed25519_public_mldsa87_public_sig,
          peer_kr,
          concatByteArray(ed25519_public, mldsa87_public)
        ),
        required_cu
      );
    }
    // Load shared secrets from saved or directly from signed-KEM.
    await peer_kr.update_shared_secrets(
      self_kr,
      mlkem1024_ss_self,
      mlkem1024_ss_peer,
      mlkem1024_ss_peer_sig
    );
    // Check the fingerprint of the root signing key.
    if (trust_fingerprint) {
      // If there is an explicit identity-trust fingerprint, the TOFU fingerprints are irrelevant.
      await peer_kr.update(trust_fingerprint, self_kr);
      return peer_kr;
    } else {
      // Generate the TOFU fingerprints for validation and storage.
      await peer_kr.create_tofu_fingerprints();
      if (stored_tofu_fingerprints) {
        for (let key_version of PeerAccountKeyring.TRUST_KEY_VERSIONS) {
          if (!stored_tofu_fingerprints[key_version]) {
            continue; // this is the first time this peer keyring is loaded
          }
          // NOTE: Naive comparison should not pose much threat here. Loading a keyring is initiated by the
          // client and an attacker cannot measure when it "fails". (especially because it is cached)
          // In case the stored key is missing from the keyring we also flag broken trust.
          if (
            !peer_kr.tofu_fingerprints[key_version] ||
            !compareByteArray(
              stored_tofu_fingerprints[key_version],
              peer_kr.tofu_fingerprints[key_version]
            )
          ) {
            peer_kr.trusted = PeerAccountKeyring.TRUSTED_NOT;
            break;
          }
        }
      }
      return peer_kr;
    }
  }

  // Updating the trust state after loading shall only be done when a user explicitly verifies a peer.
  public async update(trust_fingerprint: Uint8Array, self_kr: AbstractSelfAccountKeyring) {
    try {
      await ACCOUNT_TRUST_CRYPTO_CONTEXT.load(
        new AccountTrustConfigLoadArgs(trust_fingerprint, self_kr, this)
      );

      this.trusted = AbstractAccountKeyring.TRUSTED_FULLY;
    } catch (e) {
      if (e instanceof TrustIsStale) {
        this.trusted = AbstractAccountKeyring.TRUSTED_STALE;
      } else if (e instanceof TrustNeedsUpgrade) {
        // The upgrade may need to be done manually if the fingerprint is for an ancient trust root.
        this.trusted = AbstractAccountKeyring.TRUSTED_REQUIRES_UPGRADE;
      } else /*if (e instanceof TrustError || e instanceof UnknownCryptoConfigVersion)*/ {
        // When the verification fails in an "expected" way the keyring can be constructed and examined
        // to see what the problem is, but must not be used!
        this.trusted = AbstractAccountKeyring.TRUSTED_NOT;
      }
    }
  }

  /**
   * For legacy api compatibility. Prefer .load_anonymous_keys()
   */
  public static load_anonymous(
    self_kr: AbstractSelfAccountKeyring,
    curve25519_public: Uint8Array,
    mlkem1024_ss_peer?: Uint8Array
  ) {
    const items = { [PeerAccountKeyring.CURVE25519]: curve25519_public };
    if (mlkem1024_ss_peer) {
      items[PeerAccountKeyring.SS_PEER_MLKEM1024] = mlkem1024_ss_peer;
    }
    return this.load_anonymous_keys(self_kr, items);
  }

  /**
     * Loading an anonymous peer account's keyring has no checks for authenticity because the peer is unknown.
        The caller code must keep in mind that a keyring is for an anonymous peer and work accordingly.
     */
  public static load_anonymous_keys(self_kr: AbstractSelfAccountKeyring, items: Object) {
    const public_keys = {};
    const shared_secrets = {};
    // Only load keys that are known to be for ECDH/KEM purpose. Trust-keys are not usable for anonymous and
    // newer (not yet implemented) or obsolete ECDH/KEM keys should not be used.
    if (items[AbstractAccountKeyring.CURVE25519]) {
      public_keys[AbstractAccountKeyring.CURVE25519] = items[AbstractAccountKeyring.CURVE25519];
    }
    if (items[AbstractPeerAccountKeyring.SS_PEER_MLKEM1024]) {
      shared_secrets[AbstractPeerAccountKeyring.SS_PEER_MLKEM1024] = this._unpack_eph_kem(
        self_kr.get_secret_key(AbstractAccountKeyring.MLKEM1024),
        items[AbstractPeerAccountKeyring.SS_PEER_MLKEM1024]
      );
    }
    return new PeerAccountKeyring(
      AbstractAccountKeyring.ANONYMOUS_ID,
      public_keys,
      AbstractAccountKeyring.TRUSTED_UNKNOWN,
      shared_secrets
    );
  }

  // TODO @functools.lru_cache(maxsize=500)
  protected static _unpack_eph_kem(secret_key: Uint8Array, ciphertext: Uint8Array): Uint8Array {
    return MLKEM1024KEM.unpack(secret_key, ciphertext);
  }

  public async update_shared_secrets(
    self_kr: AbstractSelfAccountKeyring,
    mlkem1024_ss_self?: Uint8Array, // generated and encrypted by self_kr
    mlkem1024_ss_peer?: Uint8Array, // generated (+_sig) by peer; later encrypted by self_kr (optimized)
    mlkem1024_ss_peer_sig?: Uint8Array // signature by peer
  ): Promise<boolean> {
    const shared_secrets = {};
    if (this.get_variant() === AbstractSelfAccountKeyring.VARIANT_ML) {
      if (mlkem1024_ss_self) {
        shared_secrets[AbstractPeerAccountKeyring.SS_SELF_MLKEM1024] =
          await SHARED_SECRET_CRYPTO_CONTEXT.load(
            new SharedSecretConfigLoadArgs(
              mlkem1024_ss_self,
              self_kr,
              this,
              AbstractPeerAccountKeyring.SS_SELF_MLKEM1024
            )
          );
      }
      if (mlkem1024_ss_peer_sig && mlkem1024_ss_peer) {
        shared_secrets[AbstractPeerAccountKeyring.SS_PEER_MLKEM1024] = MLKEM1024KEM.unpack(
          self_kr.get_secret_key(AbstractSelfAccountKeyring.MLKEM1024),
          await ACCOUNT_SIGNATURE_CRYPTO_CONTEXT.load(
            new AccountSignatureConfigLoadArgs(mlkem1024_ss_peer_sig, this, mlkem1024_ss_peer),
            CU_PQC_ML
          )
        );
      } else if (mlkem1024_ss_peer) {
        shared_secrets[AbstractPeerAccountKeyring.SS_PEER_MLKEM1024] =
          await SHARED_SECRET_CRYPTO_CONTEXT.load(
            new SharedSecretConfigLoadArgs(
              mlkem1024_ss_peer,
              self_kr,
              this,
              AbstractPeerAccountKeyring.SS_PEER_MLKEM1024
            )
          );
      }
    }
    if (Object.keys(shared_secrets).length > 0) {
      AbstractSelfAccountKeyring.check_key_lengths(
        shared_secrets,
        AbstractPeerAccountKeyring.VALID_SHARED_SECRET_LENGTH_MAP
      );
      for (let key in shared_secrets) {
        this._shared_secrets[key] = shared_secrets[key];
      }
      return true;
    }
    return false;
  }

  /**
   * Create a new KEM to be used with peer_kr. Returns the SS encrypted for self and signed KEM for peer.
   *   NOTE: The client must save these on the server and only use them after they are confirmed to be stored,
   *         otherwise the recipient accounts may not be able to receive the KEM along with the payload.
   *   NOTE: Even though the peer is enough to have MLKEM keys for the shared secret creation, we sign the ciphertext
   *         which requires the self keyring to have ML keys as well.
   */
  public async generate_kem(
    self_kr: AbstractSelfAccountKeyring,
    version: string
  ): Promise<{ enc_ss: Uint8Array; kem: Uint8Array; kem_signature: Uint8Array }> {
    let enc_ss: Uint8Array, kem: Uint8Array, kem_signature: Uint8Array;
    if (version === AbstractSelfAccountKeyring.MLKEM1024) {
      if (self_kr.get_variant() === AbstractSelfAccountKeyring.VARIANT_BASE) {
        throw new ArgumentsError('Self keyring missing ML keys');
      }
      const tmp = MLKEM1024KEM.create(this.get_public_key(AbstractSelfAccountKeyring.MLKEM1024));
      kem = tmp.cipherText;
      kem_signature = await ACCOUNT_SIGNATURE_CRYPTO_CONTEXT.dump(
        new AccountSignatureConfigDumpArgs(kem, self_kr),
        CU_PQC_ML
      );
      enc_ss = await SHARED_SECRET_CRYPTO_CONTEXT.dump(
        new SharedSecretConfigDumpArgs(
          tmp.sharedSecret,
          self_kr,
          this,
          AbstractPeerAccountKeyring.SS_SELF_MLKEM1024
        )
      );
    } else {
      throw new ArgumentsError('Key version is not KEM');
    }
    return { enc_ss, kem, kem_signature };
  }

  /**
   * Save the peer-kem as self-encrypted.
   */
  public async resave_kem(
    self_kr: AbstractSelfAccountKeyring,
    version: string
  ): Promise<Uint8Array> {
    let enc_p_ss: Uint8Array;
    if (version === AbstractSelfAccountKeyring.MLKEM1024) {
      if (self_kr.get_variant() === AbstractSelfAccountKeyring.VARIANT_BASE) {
        throw new ArgumentsError('Self keyring missing ML keys');
      }
      enc_p_ss = await SHARED_SECRET_CRYPTO_CONTEXT.dump(
        new SharedSecretConfigDumpArgs(
          this.get_shared_secret(AbstractPeerAccountKeyring.SS_PEER_MLKEM1024),
          self_kr,
          this,
          AbstractPeerAccountKeyring.SS_PEER_MLKEM1024
        )
      );
    } else {
      throw new ArgumentsError('Key version is not KEM');
    }
    return enc_p_ss;
  }

  /**
   * Create a new KEM to be used between the parties.
   *   Can be used to prepare shared secret for target peer and get ML-KEM ciphertext to be sent with the
   *   encrypted request.
   *   A new peer_kr is returned with the new SS loaded into it, this makes usage in client cleaner, where
   *   a peer and room may be addressed both with logged in and anonymous requests.
   *   This is also useful for creating challenges by the server, i.e. when both parties are practically
   *   anonymous still.
   */
  public async generate_anonymous_kem(
    self_kr: AbstractSelfAccountKeyring,
    version: string
  ): Promise<{ kem: Uint8Array; peer_kr: PeerAccountKeyring }> {
    let kem: Uint8Array, peer_kr: PeerAccountKeyring;
    if (version === AbstractSelfAccountKeyring.MLKEM1024) {
      if (self_kr.get_variant() === AbstractSelfAccountKeyring.VARIANT_BASE) {
        throw new ArgumentsError('Self keyring missing ML keys');
      }
      const tmp = MLKEM1024KEM.create(this.get_public_key(AbstractSelfAccountKeyring.MLKEM1024));
      kem = tmp.cipherText;
      peer_kr = new PeerAccountKeyring(
        new TextDecoder().decode(this.id),
        Object.assign({}, this._public_keys),
        this.trusted,
        {
          [PeerAccountKeyring.SS_SELF_MLKEM1024]: tmp.sharedSecret,
        }
      );
    } else {
      throw new ArgumentsError('Key version is not KEM');
    }
    return { kem, peer_kr };
  }
}
